import dayjs from 'dayjs';

/**
 * Generic method to determine if the input object is undefined, null, or empty
 * @param obj
 * @returns
 * 	True indicating the input object was undefined, null, or empty
 * 	False indicating the input object has data
 */
export function isObjectNullOrEmpty(obj?: object) {
	if (!obj) return true;
	return Object.keys(obj).length === 0;
}

/**
 * Generic method to retrieve an object property by a string key
 * @param obj Object to be used
 * @param keyValue String value of the property key
 * @returns Property value
 */
export function getObjectEntryByKey<T>(obj: T, keyValue: keyof T) {
	return obj[keyValue] ?? null;
}

/**
 * Generic method for getting only the mutated properties of an object. Used for posting http requests to the API
 * with only the altered fields.
 * @param values The mutated object values
 * @param initialValues The original values of the object
 * @returns Partial object containing only the mutated properties
 */
export function getObjectChanges<T extends Record<string, unknown>>(values: T, initialValues: T | undefined) {
	return Object.entries(values).reduce<Partial<T>>((result, [key, value]) => {
		// We need to explicitly handle Dayjs instances
		if (value instanceof dayjs) {
			if (initialValues && value.valueOf() !== initialValues[key]?.valueOf()) {
				return {
					...result,
					[key]: value
				};
			} else {
				return result;
			}
		}

		// If the value is an object we need to do recursion
		if (typeof value === 'object') {
			// Handle collections specifically
			if (Array.isArray(value) || Array.isArray(initialValues)) {
				if (!areArraysEqual(value as [], initialValues ? (initialValues[key] as []) : [])) {
					return {
						...result,
						[key]: value
					};
				} else {
					// They were deemed equal, move on
					return result;
				}
			}

			if (value === null || !initialValues || initialValues[key] === null) {
				// If both properties are null just continue
				if (value === null && (!initialValues || initialValues[key] === null)) {
					return result;
				}
				// Otherwise take whatever value is whether that be null or a new object
				return {
					...result,
					[key]: value
				};
			}

			const obj = value as Record<string, unknown>;
			const obj2 = initialValues[key] as Record<string, unknown>;
			const subobjectChanges: Record<string, unknown> = getObjectChanges(obj, obj2);

			if (Object.keys(subobjectChanges).length === 0) return result;

			return {
				...result,
				[key]: { ...subobjectChanges }
			};
		}

		if (initialValues && value !== initialValues[key]) {
			return {
				...result,
				[key]: value
			};
		}

		return result;
	}, {});
}

/** Shallow Comparison of collections */
export function areArraysEqual<T>(arr1?: T[], arr2?: T[]) {
	if (!arr1 || !arr2) {
		return arr1 === arr2;
	}

	// If the collections are not the same length they are not identical
	if (arr1.length !== arr2.length) {
		return false;
	}

	for (let i = 0; i < arr1.length; i++) {
		const itemDifferences = getObjectChanges(arr1[i] as Record<string, unknown>, arr2[i] as Record<string, unknown>);
		if (!isObjectNullOrEmpty(itemDifferences)) {
			return false;
		}
	}

	// Collections are identical
	return true;
}

/** Function to remove properties with undefined values from an object recursively */
export function removeUndefinedProperties<T extends Record<string, unknown>>(obj: T) {
	Object.keys(obj).forEach((key) => {
		const propValue = obj[key];

		// If the value is undefined, remove it from the request
		if (propValue === undefined) {
			// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
			delete obj[key];
			return;
		}

		// If the value is an object (but not null), recursively clean it
		if (typeof propValue === 'object' && propValue !== null) {
			removeUndefinedProperties(propValue as Record<string, unknown>);

			// After cleaning, if it's now empty, remove it
			if (isObjectNullOrEmpty(propValue))
				// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
				delete obj[key];
		}
	});

	return obj;
}

export function hasRequiredProperty<T extends object, K extends keyof T>(obj: T, prop: K): obj is T & Required<Pick<T, K>> {
	return prop in obj && obj[prop] !== undefined;
}
